import { auth } from '@/lib/firebase'
import { getUserCompanies } from '@/services/companies'
import { getUserById } from '@/services/users'
import type { Company, User as MyUser } from '@/types'
import type { CompanyUserRole } from '@/types/companyUser'
import { UserType } from '@/types/user'
import { type User, onAuthStateChanged } from 'firebase/auth'
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

// Cache keys for localStorage
const CACHE_KEYS = {
  USER_DATA: 'auth_user_data',
  COMPANY_DATA: 'auth_company_data',
  USER_ROLE: 'auth_user_role',
  COMPANIES_LIST: 'auth_companies_list',
  CACHE_TIMESTAMP: 'auth_cache_timestamp',
}

// Cache duration: 10 minutes
const CACHE_DURATION = 10 * 60 * 1000

interface UserContextType {
  userData: MyUser | null
  setUserData: Dispatch<SetStateAction<MyUser | null>>
  currentUser: User | null
  setCurrentUser: Dispatch<SetStateAction<User | null>>
  userRole: CompanyUserRole | null
  refreshUserRole: () => Promise<void>
  refreshUserData: () => Promise<void>
  isAuthReady: boolean // Add this to know when auth is fully loaded
}

interface CompanyContextType {
  company: Company | null
  setCompany: Dispatch<SetStateAction<Company | null>>
}

interface LoadingContextType {
  isLoading: boolean
}

const UserContext = createContext<UserContextType>({
  userData: null,
  setUserData: () => void 0,
  currentUser: null,
  setCurrentUser: () => void 0,
  userRole: null,
  refreshUserRole: async () => {},
  refreshUserData: async () => {},
  isAuthReady: false,
})

const CompanyContext = createContext<CompanyContextType>({
  company: null,
  setCompany: () => void 0,
})

const LoadingContext = createContext<LoadingContextType>({
  isLoading: false,
})

export const useUser = () => useContext(UserContext)
export const useCompany = () => useContext(CompanyContext)
export const useAuthLoading = () => useContext(LoadingContext)

export const useAuth = () => ({
  ...useUser(),
  ...useCompany(),
  ...useAuthLoading(),
})

// Cache utilities
const getCachedData = (key: string) => {
  if (typeof window === 'undefined') return null
  try {
    const cached = localStorage.getItem(key)
    return cached ? JSON.parse(cached) : null
  } catch {
    return null
  }
}

const setCachedData = (key: string, data: any) => {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(key, JSON.stringify(data))
    localStorage.setItem(CACHE_KEYS.CACHE_TIMESTAMP, Date.now().toString())
  } catch {
    // localStorage might be full or disabled
  }
}

const isCacheValid = () => {
  if (typeof window === 'undefined') return false
  const timestamp = localStorage.getItem(CACHE_KEYS.CACHE_TIMESTAMP)
  if (!timestamp) return false
  return Date.now() - Number.parseInt(timestamp) < CACHE_DURATION
}

const clearCache = () => {
  if (typeof window === 'undefined') return
  for (const key of Object.values(CACHE_KEYS)) {
    localStorage.removeItem(key)
  }
}

// Export cache clearing for external use (e.g., after user updates)
export const clearAuthCache = clearCache

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [company, setCompany] = useState<Company | null>(null)
  const [userData, setUserData] = useState<MyUser | null>(null)
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [userRole, setUserRole] = useState<CompanyUserRole | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthReady, setIsAuthReady] = useState(false)

  // Load cached data on mount
  useEffect(() => {
    if (isCacheValid()) {
      const cachedUserData = getCachedData(CACHE_KEYS.USER_DATA)
      const cachedCompanyData = getCachedData(CACHE_KEYS.COMPANY_DATA)
      const cachedUserRole = getCachedData(CACHE_KEYS.USER_ROLE)

      if (cachedUserData) {
        console.log('Loading cached user data')
        setUserData(cachedUserData)
      }
      if (cachedCompanyData) {
        console.log('Loading cached company data')
        setCompany(cachedCompanyData)
      }
      if (cachedUserRole) {
        setUserRole(cachedUserRole)
      }
    } else {
      clearCache()
    }
  }, [])

  const fetchUserData = useCallback(async (uid: string, forceRefresh = false) => {
    try {
      // Check cache first if not forcing refresh
      if (!forceRefresh && isCacheValid()) {
        const cached = getCachedData(CACHE_KEYS.USER_DATA)
        if (cached) {
          console.log('Using cached user data')
          setUserData(cached)
          return cached
        }
      }

      console.log('Fetching fresh user data')
      const user = await getUserById(uid)
      setUserData(user)
      setCachedData(CACHE_KEYS.USER_DATA, user)
      return user
    } catch (error) {
      console.error('Error fetching user data:', error)
      setUserData(null)
    }
  }, [])

  const refreshUserData = useCallback(async () => {
    if (!currentUser?.uid) return

    try {
      await fetchUserData(currentUser.uid, true) // Force refresh
    } catch (error) {
      console.error('Error refreshing user data:', error)
    }
  }, [currentUser?.uid, fetchUserData])

  const fetchCompanyData = useCallback(async (uid: string, userData?: MyUser | null, forceRefresh = false) => {
    try {
      // Check cache first if not forcing refresh
      if (!forceRefresh && isCacheValid()) {
        const cachedCompany = getCachedData(CACHE_KEYS.COMPANY_DATA)
        const cachedRole = getCachedData(CACHE_KEYS.USER_ROLE)
        if (cachedCompany && cachedRole) {
          console.log('Using cached company data')
          setCompany(cachedCompany)
          setUserRole(cachedRole)
          return
        }
      }

      const { companies, roles } = await getUserCompanies(uid)
      if (companies?.length) {
        const firstCompany = companies[0]
        setCompany(firstCompany)
        setCachedData(CACHE_KEYS.COMPANY_DATA, firstCompany)

        const firstCompanyRole = roles.get(firstCompany.id || '')
        const roleValue = firstCompanyRole?.role || null
        setUserRole(roleValue)
        setCachedData(CACHE_KEYS.USER_ROLE, roleValue)

        if (userData && firstCompany.id && !userData.companyId) {
          const updatedUserData = { ...userData, companyId: firstCompany.id }
          setUserData(updatedUserData)
          setCachedData(CACHE_KEYS.USER_DATA, updatedUserData)
        }
      } else {
        // Check if user previously had a company (to show transfer notification)
        const hadCompanyBefore = !!getCachedData(CACHE_KEYS.COMPANY_DATA)

        setCompany(null)
        setUserRole(null)

        // Clear company-related cache but keep user data
        localStorage.removeItem('rtsc_cache_company_data')
        localStorage.removeItem('rtsc_cache_user_role')

        // Show notification if user previously had access (ownership was transferred)
        if (hadCompanyBefore && userData && !userData.isRoot && userData.userType !== UserType.SUPER_ADMIN) {
          // Import toast dynamically to avoid SSR issues
          import('sonner').then(({ toast }) => {
            toast.info('Company ownership has been transferred. You no longer have access to company features.')
          })
        }
      }
    } catch (error) {
      console.error('Error fetching company data:', error)
      setCompany(null)
      setUserRole(null)
    }
  }, [])

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setIsLoading(true)
      setCurrentUser(firebaseUser)

      if (!firebaseUser) {
        setCompany(null)
        setUserData(null)
        setUserRole(null)
        clearCache()
        setIsLoading(false)
        setIsAuthReady(true)
        return
      }

      try {
        // Try to load from cache first
        const shouldUseFreshData = !isCacheValid()

        const userData = await fetchUserData(firebaseUser.uid, shouldUseFreshData)
        await fetchCompanyData(firebaseUser.uid, userData, shouldUseFreshData)

        setIsAuthReady(true)
      } catch (error) {
        console.error('Error in auth state change:', error)
        setIsAuthReady(true)
      } finally {
        setIsLoading(false)
      }
    })

    return unsubscribe
  }, [fetchUserData, fetchCompanyData])

  const refreshUserRole = useCallback(async () => {
    if (!currentUser?.uid) return

    try {
      const userData = await fetchUserData(currentUser.uid, true) // Force refresh
      await fetchCompanyData(currentUser.uid, userData, true) // Force refresh
    } catch (error) {
      console.error('Error refreshing user role:', error)
    }
  }, [currentUser?.uid, fetchUserData, fetchCompanyData])

  const userValue = useMemo(
    () => ({
      userData,
      setUserData,
      currentUser,
      setCurrentUser,
      userRole,
      refreshUserRole,
      refreshUserData,
      isAuthReady,
    }),
    [userData, currentUser, userRole, refreshUserRole, refreshUserData, isAuthReady],
  )

  const companyValue = useMemo(
    () => ({
      company,
      setCompany,
    }),
    [company],
  )

  const loadingValue = useMemo(
    () => ({
      isLoading,
    }),
    [isLoading],
  )

  return (
    <UserContext.Provider value={userValue}>
      <CompanyContext.Provider value={companyValue}>
        <LoadingContext.Provider value={loadingValue}>{children}</LoadingContext.Provider>
      </CompanyContext.Provider>
    </UserContext.Provider>
  )
}
